import React, { useState, useEffect, useCallback } from 'react';
import { permissionService, authService, settingsService } from '../../services';
import { usePageTitle } from '../../context/SiteSettingsContext';
import './PermissionManagement.css';

function PermissionManagement() {
  usePageTitle('权限管理');
  const [users, setUsers] = useState([]);
  const [permissionTypes, setPermissionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRole, setUserRole] = useState('user');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [registrationOpen, setRegistrationOpen] = useState(true);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await permissionService.getAllUsersWithPermissions(currentPage, 10, searchTerm);
      setUsers(data.users);
      setTotalPages(data.pagination.totalPages);
    } catch (err) {
      setError('加载用户列表失败');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  const loadPermissionTypes = useCallback(async () => {
    try {
      const types = await permissionService.getPermissionTypes();
      setPermissionTypes(types);
    } catch (err) {
      console.error('加载权限类型失败:', err);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const data = await settingsService.getRegistrationStatus();
      setRegistrationOpen(data.registrationOpen);
    } catch (err) {
      console.error('加载设置失败:', err);
    }
  }, []);

  useEffect(() => {
    loadPermissionTypes();
    loadSettings();
  }, [loadPermissionTypes, loadSettings]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadUsers();
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setUserPermissions(user.permissions || []);
    setUserRole(user.role || 'user');
    setSuccessMessage('');
  };

  const handlePermissionToggle = (permissionKey) => {
    setUserPermissions(prev => {
      if (prev.includes(permissionKey)) {
        return prev.filter(p => p !== permissionKey);
      } else {
        return [...prev, permissionKey];
      }
    });
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    
    try {
      setSaving(true);
      setError('');
      
      // Update role if changed
      if (userRole !== selectedUser.role) {
        await authService.updateUserRole(selectedUser.id, userRole);
      }

      await permissionService.updateUserPermissions(selectedUser.id, userPermissions);
      
      setSuccessMessage('权限保存成功！');
      
      // Reload users to get updated data
      loadUsers();
    } catch (err) {
      setError('保存权限失败：' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const toggleRegistration = async () => {
    try {
      const newState = !registrationOpen;
      await settingsService.updateRegistrationStatus(newState);
      setRegistrationOpen(newState);
    } catch (err) {
      setError('更新注册状态失败');
    }
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
    setUserPermissions([]);
    setUserRole('user');
    setSuccessMessage('');
    setError('');
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return '管理员';
      case 'premium': return '主播';
      default: return '普通用户';
    }
  };

  const getPermissionLabel = (permissions) => {
    if (!permissions || permissions.length === 0) return '无权限';
    return `${permissions.length} 个权限`;
  };

  return (
    <div className="permission-management">
      <div className="page-header">
        <h1>🔐 权限管理</h1>
        <div className="registration-toggle">
          <span>注册状态：</span>
          <button 
            className={`toggle-btn ${registrationOpen ? 'open' : 'closed'}`}
            onClick={toggleRegistration}
          >
            {registrationOpen ? '开放' : '关闭'}
          </button>
        </div>
      </div>
      
      <div className="search-bar">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="搜索用户名或邮箱..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="submit">搜索</button>
        </form>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">加载中...</div>
      ) : (
        <>
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>用户名</th>
                  <th>邮箱</th>
                  <th>角色</th>
                  <th>权限</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className={user.role === 'admin' ? 'admin-row' : ''}>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge ${user.role}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td>
                      {user.role === 'admin' ? (
                        <span className="all-permissions">全部权限</span>
                      ) : (
                        <span className="permission-count">
                          {getPermissionLabel(user.permissions)}
                        </span>
                      )}
                    </td>
                    <td>
                      {user.role !== 'admin' && (
                        <button 
                          className="edit-btn"
                          onClick={() => handleSelectUser(user)}
                        >
                          编辑权限
                        </button>
                      )}
                      {user.role === 'admin' && (
                        <span className="admin-note">管理员无需配置</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              上一页
            </button>
            <span>第 {currentPage} / {totalPages} 页</span>
            <button 
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              下一页
            </button>
          </div>
        </>
      )}

      {/* Permission Edit Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="permission-modal" onClick={(e) => e.stopPropagation()}>
            <h2>编辑用户权限</h2>
            <div className="user-info">
              <strong>{selectedUser.username}</strong>
              <span>{selectedUser.email}</span>
            </div>

            {successMessage && (
              <div className="success-message">{successMessage}</div>
            )}
            {error && <div className="error-message">{error}</div>}

            <div className="role-section">
              <h3>用户角色</h3>
              <div className="role-selector">
                <label className={`role-option ${userRole === 'user' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="role"
                    value="user"
                    checked={userRole === 'user'}
                    onChange={() => setUserRole('user')}
                  />
                  <span className="role-label">普通用户</span>
                </label>
                <label className={`role-option ${userRole === 'premium' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="role"
                    value="premium"
                    checked={userRole === 'premium'}
                    onChange={() => setUserRole('premium')}
                  />
                  <span className="role-label">主播</span>
                </label>
              </div>
            </div>

            <div className="permissions-section">
              <h3>功能权限</h3>
              <div className="permissions-grid">
                {permissionTypes.map(perm => (
                  <label key={perm.key} className="permission-item">
                    <input
                      type="checkbox"
                      checked={userPermissions.includes(perm.key)}
                      onChange={() => handlePermissionToggle(perm.key)}
                    />
                    <div className="permission-info">
                      <span className="permission-name">{perm.name}</span>
                      <span className="permission-desc">{perm.description}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={handleCloseModal}
              >
                取消
              </button>
              <button 
                className="save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存权限'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PermissionManagement;
